import React, { useState } from 'react';
import { Scale, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, AlertTriangle, HelpCircle, Calculator } from 'lucide-react';
import { Input } from '../ui/input';
import { Toggle } from '../ui/toggle';
import { Button } from '../ui/button';
import { Section } from './InspectionShared';

const HEFT_OPTIONS = [
  { value: 'Light', label: 'Light', info: 'Hive feels very easy to tilt. Low stores — may need feeding soon. Typically under 25 kg / 55 lbs total.', color: 'bg-red-100 text-red-700 data-[state=on]:bg-red-600' },
  { value: 'Medium', label: 'Medium', info: 'Moderate resistance when tilting. Adequate stores for now but worth monitoring. Typically 25–40 kg / 55–88 lbs.', color: 'bg-amber-100 text-amber-700 data-[state=on]:bg-amber-600' },
  { value: 'Heavy', label: 'Heavy', info: 'Hard to tilt — significant stores present. The colony is well-provisioned. Typically over 40 kg / 88 lbs.', color: 'bg-green-100 text-green-700 data-[state=on]:bg-green-600' },
];

const TiltCalculator = ({ onResult, unit }) => {
  const [mode, setMode] = useState('single'); // 'single' | 'front-back'
  const [singleSide, setSingleSide] = useState('');
  const [frontTilt, setFrontTilt] = useState('');
  const [backTilt, setBackTilt] = useState('');

  const calcSingle = () => {
    const w = parseFloat(singleSide);
    if (!w || w <= 0) return;
    onResult((w * 2).toFixed(1));
  };

  const calcFrontBack = () => {
    const f = parseFloat(frontTilt);
    const b = parseFloat(backTilt);
    if (!f || !b || f <= 0 || b <= 0) return;
    onResult((f + b).toFixed(1));
  };

  return (
    <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-indigo-800">
        <Calculator className="h-4 w-4" /> Tilt-Weigh Calculator
      </div>
      <div className="flex gap-2">
        <Toggle pressed={mode === 'single'} onPressedChange={() => setMode('single')} className="h-7 px-3 text-xs data-[state=on]:bg-indigo-600 data-[state=on]:text-white">One side × 2</Toggle>
        <Toggle pressed={mode === 'front-back'} onPressedChange={() => setMode('front-back')} className="h-7 px-3 text-xs data-[state=on]:bg-indigo-600 data-[state=on]:text-white">Front + Back</Toggle>
      </div>
      {mode === 'single' ? (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-[10px] text-indigo-600 mb-0.5 block">Scale reading (one side)</label>
            <Input type="number" step="0.1" value={singleSide} onChange={e => setSingleSide(e.target.value)} placeholder={unit === 'kg' ? '22.5' : '49.6'} className="h-9 text-sm" data-testid="tilt-single-input" />
          </div>
          <Button type="button" size="sm" onClick={calcSingle} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9 text-xs" data-testid="tilt-calc-btn">
            = {singleSide ? `${(parseFloat(singleSide || 0) * 2).toFixed(1)} ${unit}` : `? ${unit}`}
          </Button>
        </div>
      ) : (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-[10px] text-indigo-600 mb-0.5 block">Front tilt</label>
            <Input type="number" step="0.1" value={frontTilt} onChange={e => setFrontTilt(e.target.value)} placeholder="22.0" className="h-9 text-sm" data-testid="tilt-front-input" />
          </div>
          <div className="flex-1">
            <label className="text-[10px] text-indigo-600 mb-0.5 block">Back tilt</label>
            <Input type="number" step="0.1" value={backTilt} onChange={e => setBackTilt(e.target.value)} placeholder="23.0" className="h-9 text-sm" data-testid="tilt-back-input" />
          </div>
          <Button type="button" size="sm" onClick={calcFrontBack} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9 text-xs" data-testid="tilt-calc-btn">
            = {frontTilt && backTilt ? `${(parseFloat(frontTilt || 0) + parseFloat(backTilt || 0)).toFixed(1)} ${unit}` : `? ${unit}`}
          </Button>
        </div>
      )}
      <p className="text-[10px] text-indigo-500">Result will be placed into Current Weight above.</p>
    </div>
  );
};

const WeightSection = ({ formData, setFormData, handleSectionPhoto, weightSettings, setWeightSettings, baselineWeight, previousWeight, weightHistory }) => {
  const [showTiltCalc, setShowTiltCalc] = useState(false);
  const [heftTooltip, setHeftTooltip] = useState(null);

  const handleTiltResult = (result) => {
    setFormData({ ...formData, hive_weight: result });
    setShowTiltCalc(false);
  };

  return (
        <Section title="Hive Weight" icon={<Scale className="h-6 w-6 text-emerald-600" />} onPhoto={handleSectionPhoto}>
          <div className="space-y-4">
            {/* Heft Assessment (for beekeepers without scales) */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold text-slate-700">Heft Assessment</span>
                <span className="text-[10px] text-slate-400">(no scales needed)</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {HEFT_OPTIONS.map(opt => (
                  <div key={opt.value} className="relative">
                    <Toggle
                      pressed={formData.heft_weight === opt.value}
                      onPressedChange={() => setFormData({ ...formData, heft_weight: formData.heft_weight === opt.value ? '' : opt.value })}
                      className={`h-12 w-full text-sm font-semibold ${opt.color} data-[state=on]:text-white`}
                      data-testid={`heft-${opt.value.toLowerCase()}`}
                    >
                      {opt.label}
                    </Toggle>
                    <button
                      type="button"
                      className="absolute -top-1 -right-1 w-5 h-5 bg-white border border-slate-200 rounded-full flex items-center justify-center shadow-sm hover:bg-slate-50"
                      onMouseEnter={() => setHeftTooltip(opt.value)}
                      onMouseLeave={() => setHeftTooltip(null)}
                      onClick={(e) => { e.stopPropagation(); setHeftTooltip(heftTooltip === opt.value ? null : opt.value); }}
                    >
                      <HelpCircle className="h-3 w-3 text-slate-400" />
                    </button>
                    {heftTooltip === opt.value && (
                      <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-slate-800 text-white text-[10px] leading-relaxed p-2.5 rounded-lg shadow-lg">
                        {opt.info}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Weight Info Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Baseline Weight - User Editable */}
              <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                <div className="text-xs text-gray-500 mb-1">Baseline (Tare)</div>
                <Input
                  type="number"
                  step="0.1"
                  placeholder={baselineWeight ? (weightSettings.unit_preference === 'kg' 
                    ? baselineWeight.baseline_weight_kg.toString()
                    : baselineWeight.baseline_weight_lbs.toString()
                  ) : '15.0'}
                  value={formData.baseline_weight || ''}
                  onChange={(e) => setFormData({ ...formData, baseline_weight: e.target.value })}
                  data-testid="baseline-weight-input"
                  className="h-10 text-lg font-bold bg-white"
                />
                <div className="text-xs text-gray-400 mt-1">
                  {baselineWeight && !formData.baseline_weight && (
                    <span>Calculated: {weightSettings.unit_preference === 'kg' 
                      ? `${baselineWeight.baseline_weight_kg} kg`
                      : `${baselineWeight.baseline_weight_lbs} lbs`
                    }</span>
                  )}
                  {!baselineWeight && !formData.baseline_weight && 'Empty hive weight'}
                </div>
              </div>
              
              {/* Previous Weight */}
              <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
                <div className="text-xs text-blue-600 mb-1">Previous Inspection</div>
                <div className="text-lg font-bold text-blue-700">
                  {previousWeight ? (
                    weightSettings.unit_preference === 'kg' 
                      ? `${previousWeight.toFixed(1)} kg`
                      : `${(previousWeight * 2.20462).toFixed(1)} lbs`
                  ) : '-- kg'}
                </div>
                <div className="text-xs text-blue-400">
                  {weightHistory.length > 0 ? new Date(weightHistory[0].date).toLocaleDateString() : 'No records'}
                </div>
              </div>
              
              {/* Current Weight Input */}
              <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200">
                <div className="text-xs text-emerald-600 mb-1">Current Weight *</div>
                <Input
                  type="number"
                  step="0.1"
                  placeholder={weightSettings.unit_preference === 'kg' ? '45.5' : '100.3'}
                  value={formData.hive_weight || ''}
                  onChange={(e) => setFormData({ ...formData, hive_weight: e.target.value })}
                  data-testid="hive-weight-input"
                  className="h-10 text-lg font-bold"
                />
                <div className="text-xs text-emerald-400 mt-1">
                  {weightSettings.unit_preference === 'kg' ? 'kilograms' : 'pounds'}
                </div>
              </div>
              
              {/* Weight Change */}
              <div className={`rounded-xl p-3 border ${
                formData.hive_weight && previousWeight 
                  ? (parseFloat(formData.hive_weight) >= previousWeight 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200')
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="text-xs text-gray-500 mb-1">Weight Change</div>
                {formData.hive_weight && previousWeight ? (
                  <>
                    <div className={`text-lg font-bold flex items-center gap-1 ${
                      parseFloat(formData.hive_weight) >= previousWeight ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {parseFloat(formData.hive_weight) >= previousWeight 
                        ? <ArrowUpRight className="h-5 w-5" />
                        : <ArrowDownRight className="h-5 w-5" />
                      }
                      {weightSettings.unit_preference === 'kg' 
                        ? `${(parseFloat(formData.hive_weight) - previousWeight).toFixed(1)} kg`
                        : `${((parseFloat(formData.hive_weight) - previousWeight) * 2.20462).toFixed(1)} lbs`
                      }
                    </div>
                    <div className="text-xs text-gray-400">
                      {previousWeight > 0 
                        ? `${(((parseFloat(formData.hive_weight) - previousWeight) / previousWeight) * 100).toFixed(1)}%`
                        : '--'
                      }
                    </div>
                  </>
                ) : (
                  <div className="text-lg font-bold text-gray-400">-- kg</div>
                )}
              </div>
            </div>
            
            {/* Net Honey Weight */}
            {formData.hive_weight && (formData.baseline_weight || baselineWeight) && (
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-amber-600 font-medium">Estimated Stores (Honey + Bees)</div>
                    <div className="text-2xl font-bold text-amber-700">
                      {(() => {
                        const baseline = formData.baseline_weight ? parseFloat(formData.baseline_weight) : (baselineWeight?.baseline_weight_kg || 0);
                        const netWeight = parseFloat(formData.hive_weight) - baseline;
                        return weightSettings.unit_preference === 'kg' 
                          ? `${netWeight.toFixed(1)} kg`
                          : `${(netWeight * 2.20462).toFixed(1)} lbs`;
                      })()}
                    </div>
                  </div>
                  <div className="text-4xl">🍯</div>
                </div>
                <div className="text-xs text-amber-500 mt-2">
                  Current ({formData.hive_weight} {weightSettings.unit_preference}) minus baseline ({formData.baseline_weight || baselineWeight?.baseline_weight_kg || 0} {weightSettings.unit_preference})
                </div>
              </div>
            )}
            
            {/* Weight Alert Warning */}
            {formData.hive_weight && previousWeight && parseFloat(formData.hive_weight) < previousWeight && (
              <div className={`rounded-xl p-4 border ${
                (((previousWeight - parseFloat(formData.hive_weight)) / previousWeight) * 100) >= weightSettings.alert_threshold_percent
                  ? 'bg-red-50 border-red-300'
                  : 'bg-yellow-50 border-yellow-300'
              }`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`h-5 w-5 mt-0.5 ${
                    (((previousWeight - parseFloat(formData.hive_weight)) / previousWeight) * 100) >= weightSettings.alert_threshold_percent
                      ? 'text-red-600'
                      : 'text-yellow-600'
                  }`} />
                  <div className="flex-1">
                    <div className={`font-medium ${
                      (((previousWeight - parseFloat(formData.hive_weight)) / previousWeight) * 100) >= weightSettings.alert_threshold_percent
                        ? 'text-red-800'
                        : 'text-yellow-800'
                    }`}>
                      Weight Decrease Detected
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      The hive has lost {(previousWeight - parseFloat(formData.hive_weight)).toFixed(1)} {weightSettings.unit_preference} 
                      ({(((previousWeight - parseFloat(formData.hive_weight)) / previousWeight) * 100).toFixed(1)}%) since the last inspection.
                    </div>
                    
                    {/* Reason Selection */}
                    <div className="mt-3">
                      <div className="text-sm font-medium text-gray-700 mb-2">Select possible reasons:</div>
                      <div className="flex flex-wrap gap-2">
                        {['Robbing', 'Starvation', 'Swarm Prep', 'Disease', 'Seasonal Decline', 'Harvested Honey', 'Absconding', 'Varroa', 'Weather', 'Other'].map((reason) => (
                          <Toggle
                            key={reason}
                            pressed={formData.weight_loss_reasons?.includes(reason)}
                            onPressedChange={() => {
                              const current = formData.weight_loss_reasons || [];
                              const updated = current.includes(reason)
                                ? current.filter(r => r !== reason)
                                : [...current, reason];
                              setFormData({ ...formData, weight_loss_reasons: updated });
                            }}
                            data-testid={`weight-reason-${reason.toLowerCase().replace(' ', '-')}`}
                            className={`h-8 px-3 text-xs ${
                              (((previousWeight - parseFloat(formData.hive_weight)) / previousWeight) * 100) >= weightSettings.alert_threshold_percent
                                ? 'data-[state=on]:bg-red-600 data-[state=on]:text-white'
                                : 'data-[state=on]:bg-yellow-600 data-[state=on]:text-white'
                            }`}
                          >
                            {reason}
                          </Toggle>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Unit Toggle + Tilt Calculator */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="inspection-weight-unit">
                <span>Unit:</span>
                <span className="px-2.5 py-1 rounded-md bg-emerald-600 text-white text-xs font-semibold">{weightSettings.unit_preference}</span>
                <span className="text-xs text-gray-400">(set in Weights settings)</span>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setShowTiltCalc(!showTiltCalc)}
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 h-8 text-xs"
                data-testid="toggle-tilt-calc-btn">
                <Calculator className="h-3.5 w-3.5 mr-1" />
                {showTiltCalc ? 'Hide Tilt Calculator' : 'Tilt-Weigh Calculator'}
              </Button>
            </div>

            {showTiltCalc && (
              <TiltCalculator onResult={handleTiltResult} unit={weightSettings.unit_preference} />
            )}
          </div>
        </Section>
  );
};

export default WeightSection;
